import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import IconButton from "@material-ui/core/IconButton";
import MenuItem from "@material-ui/core/MenuItem";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	number: Yup.string()
		.nullable()
		.when("isGroup", {
			is: true,
			then: schema => schema.nullable(),
			otherwise: schema => schema.min(8, "Too Short!").max(50, "Too Long!"),
		}),
	email: Yup.string().email("Invalid email"),
	personType: Yup.string().nullable().oneOf(["", "PF", "PJ", null]),
	document: Yup.string().nullable().max(20, "Too Long!"),
	isBlocked: Yup.boolean(),
});

const formatDocument = value => {
	const digits = (value || "").replace(/\D/g, "");

	if (digits.length <= 11) {
		return digits
			.replace(/^(\d{3})(\d)/, "$1.$2")
			.replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
			.replace(/\.(\d{3})(\d)/, ".$1-$2")
			.slice(0, 14);
	}

	return digits
		.replace(/^(\d{2})(\d)/, "$1.$2")
		.replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
		.replace(/\.(\d{3})(\d)/, ".$1/$2")
		.replace(/(\d{4})(\d)/, "$1-$2")
		.slice(0, 18);
};

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		email: "",
		personType: "",
		document: "",
		isBlocked: false,
		extraInfo: [],
	};

	const [contact, setContact] = useState(initialState);
	const [allowContactBlocking, setAllowContactBlocking] = useState(false);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const loadPreferences = async () => {
			if (!open) return;
			try {
				const { data } = await api.get("/companies/current/preferences");
				setAllowContactBlocking(Boolean(data?.allowContactBlocking));
			} catch (err) {
				setAllowContactBlocking(false);
			}
		};

		loadPreferences();
	}, [open]);

	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				setContact(prevState => {
					return { ...prevState, ...initialValues };
				});
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					setContact(data);
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
		setAllowContactBlocking(false);
	};

	const handleSaveContact = async values => {
		try {
			const payload = { ...values };
			if (payload.isGroup) {
				delete payload.number;
			}

			if (contactId) {
				const { data } = await api.put(`/contacts/${contactId}`, payload);
				if (onSave) {
					onSave(data);
				}
				handleClose();
			} else {
				const { data } = await api.post("/contacts", payload);
				if (onSave) {
					onSave(data);
				}
				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} maxWidth="lg" scroll="paper">
				<DialogTitle id="form-dialog-title">
					{contactId
						? `${i18n.t("contactModal.title.edit")}`
						: `${i18n.t("contactModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={async (values, actions) => {
						await handleSaveContact(values);
						actions.setSubmitting(false);
					}}
				>
					{({ values, errors, touched, isSubmitting, handleChange, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								<Typography variant="subtitle1" gutterBottom>
									{i18n.t("contactModal.form.mainInfo", {
										defaultValue: "Dados do contato",
									})}
								</Typography>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.name", {
										defaultValue: "Nome",
									})}
									name="name"
									autoFocus
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									className={classes.textField}
								/>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.number", {
										defaultValue: "Número do Whatsapp",
									})}
									name="number"
									disabled={Boolean(values.isGroup)}
									error={touched.number && Boolean(errors.number)}
									helperText={
										values.isGroup
											? "Número do grupo não pode ser alterado manualmente."
											: touched.number && errors.number
									}
									placeholder="5513912344321"
									variant="outlined"
									margin="dense"
								/>
								<div>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.email", {
											defaultValue: "Email",
										})}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder="Email address"
										fullWidth
										margin="dense"
										variant="outlined"
									/>
								</div>
								{!values.isGroup && (
									<>
										<div>
											<TextField
												select
												fullWidth
												label={i18n.t("contactModal.form.personType", {
													defaultValue: "Tipo de cadastro",
												})}
												name="personType"
												value={values.personType || ""}
												onChange={handleChange}
												error={touched.personType && Boolean(errors.personType)}
												helperText={touched.personType && errors.personType}
												margin="dense"
												variant="outlined"
											>
												<MenuItem value="">
													{i18n.t("contactModal.form.personTypeOptions.notInformed", {
														defaultValue: "Não informado",
													})}
												</MenuItem>
												<MenuItem value="PF">
													{i18n.t("contactModal.form.personTypeOptions.pf", {
														defaultValue: "PF",
													})}
												</MenuItem>
												<MenuItem value="PJ">
													{i18n.t("contactModal.form.personTypeOptions.pj", {
														defaultValue: "PJ",
													})}
												</MenuItem>
											</TextField>
										</div>
										<div>
											<TextField
												fullWidth
												label={i18n.t("contactModal.form.document", {
													defaultValue: "CPF/CNPJ",
												})}
												name="document"
												value={values.document || ""}
												onChange={e =>
													setFieldValue("document", formatDocument(e.target.value))
												}
												error={touched.document && Boolean(errors.document)}
												helperText={
													(touched.document && errors.document) ||
													i18n.t("contactModal.form.documentHelp", {
														defaultValue: "Informe apenas o CPF ou CNPJ do contato.",
													})
												}
												placeholder="000.000.000-00"
												margin="dense"
												variant="outlined"
											/>
										</div>
									</>
								)}
								{allowContactBlocking && (
									<FormControlLabel
										control={
											<Switch
												color="primary"
												checked={Boolean(values.isBlocked)}
												onChange={event =>
													setFieldValue("isBlocked", event.target.checked)
												}
											/>
										}
										label="Bloquear contato para novos atendimentos"
									/>
								)}
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.extraInfo", {
										defaultValue: "Informações adicionais",
									})}
								</Typography>

								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div
														className={classes.extraAttr}
														key={`${index}-info`}
													>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraName")}
															name={`extraInfo[${index}].name`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraValue")}
															name={`extraInfo[${index}].value`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<IconButton
															size="small"
															onClick={() => remove(index)}
														>
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<div className={classes.extraAttr}>
												<Button
													style={{ flex: 1, marginTop: 8 }}
													variant="outlined"
													color="primary"
													onClick={() => push({ name: "", value: "" })}
												>
													{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
												</Button>
											</div>
										</>
									)}
								</FieldArray>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default ContactModal;
